import React, { Component } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import Multiselect from "@cloudscape-design/components/multiselect";
import Table from "@cloudscape-design/components/table";
import FormField from "@cloudscape-design/components/form-field";
import Container from "@cloudscape-design/components/container";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Textarea from "@cloudscape-design/components/textarea";
import Tabs from "@cloudscape-design/components/tabs";
import Toggle from "@cloudscape-design/components/toggle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { tournamentsEditModalStore } from "../stores/tournamentsEditModal";
import {
	DAILY_RACE_PLACES,
	createDailyRacePrizeRows,
	distributeAllPrizes,
	distributeEmptyPrizes,
	formatKopecks,
	formatKopecksInput,
	parseRubToKopecks,
} from "../helpers/dailyRacePrizes";
import { tournamentsStore } from "../stores/tournaments";
import { gameStore } from "../stores/games";
import Poster from "../assets/poster.png";
import ProgressiveImage from "react-progressive-graceful-image";

const PROVIDER_IDS_KEY = "provider_ids";
const GAME_IDS_KEY = "game_ids";
const DAILY_RACE_TOURNAMENT_NAME = "dailyRace";
const MAX_TOURNAMENT_BOTS = 10;
const DAILY_RACE_TARGET_MULTIPLIER = 100;
// Sum of winnings amounts are entered in rubles and stored in kopecks.
const WINNINGS_AMOUNT_MULTIPLIER = 100;

class TournamentsEditModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			games: [],
			filteredGames: [],
			filterProvider: [],
			prizeDistributionJson: "", // JSON-строка для редактора
			jsonError: "", // Ошибка валидации JSON
			gameProviderDraft: "",
			gameProviderError: "",
			gameProviderTabId: "regular",
			botConfigDraft: "",
			botConfigError: "",
			botConfigTabId: "regular",
		};
	}

	getSelectionValue = (item) => {
		if (item && typeof item === "object" && "value" in item) {
			return item.value;
		}

		return item;
	};

	getSelectionLabel = (item, fallbackValue) => {
		if (item && typeof item === "object" && typeof item.label === "string" && item.label.trim()) {
			return item.label;
		}

		return fallbackValue;
	};

	getSelectedIds = (selection = []) => selection
		.map((item) => this.getSelectionValue(item))
		.filter((item) => item !== undefined && item !== null && item !== "")
		.map((item) => item.toString());

	getProviderOption = (provider) => ({
		value: provider.id.toString(),
		label: `${provider.name} (${provider.id})`,
	});

	getGameOption = (gameItem) => ({
		value: gameItem.gameID.toString(),
		label: `${gameItem.name} (gameID: ${gameItem.gameID})`,
	});

	getProviderOptions = (providers = []) => providers.map((provider) => this.getProviderOption(provider));

	getGameOptionsByProviders = (selectedProviders) => {
		const { game } = this.props;
		const selectedProviderIds = new Set(this.getSelectedIds(selectedProviders));

		return game.data
			.filter((gameItem) => selectedProviderIds.has(gameItem.providerID.toString()) && gameItem.active === 1)
			.map((gameItem) => this.getGameOption(gameItem));
	};

	prepareProviderSelection = (selection = [], providers = this.props.game.providers) => {
		const providerMap = new Map(providers.map((provider) => [provider.id.toString(), provider]));

		return selection.map((item) => {
			const providerId = this.getSelectionValue(item).toString();
			const provider = providerMap.get(providerId);

			if (provider) {
				return this.getProviderOption(provider);
			}

			return {
				value: providerId,
				label: this.getSelectionLabel(item, providerId),
			};
		});
	};

	prepareGameSelection = (selection = [], games = this.props.game.data) => {
		const gameMap = new Map(games.map((gameItem) => [gameItem.gameID.toString(), gameItem]));

		return selection.map((item) => {
			const gameId = this.getSelectionValue(item).toString();
			const gameItem = gameMap.get(gameId);

			if (gameItem) {
				return this.getGameOption(gameItem);
			}

			return {
				value: gameId,
				label: this.getSelectionLabel(item, gameId),
			};
		});
	};

	buildGameProviderDraft = (
		providerSelection = this.props.tournamentsEditModal.provider_ids,
		gameSelection = this.props.tournamentsEditModal.game_ids
	) => JSON.stringify(
		{
			[PROVIDER_IDS_KEY]: this.getSelectedIds(providerSelection).map((id) => Number(id)),
			[GAME_IDS_KEY]: this.getSelectedIds(gameSelection).map((id) => Number(id)),
		},
		null,
		2
	);

	isDailyRaceTournament = (tournament = this.props.tournamentsEditModal) =>
		(tournament.name || "").toLowerCase() === DAILY_RACE_TOURNAMENT_NAME.toLowerCase();

	isSumOfWinningsTournament = (tournament = this.props.tournamentsEditModal) => {
		const tournamentType = tournament.tournamentType && typeof tournament.tournamentType === "object" ? tournament.tournamentType.value : tournament.tournamentType;

		return !this.isDailyRaceTournament(tournament) && tournamentType === "sum_of_winnings";
	};

	getEmptyBot = (slot, isDailyRaceTournament = this.isDailyRaceTournament()) => {
		if (isDailyRaceTournament) {
			return {
				slot,
				target: {
					min: "",
					max: "",
				},
			};
		}

		return {
			slot,
			targetBets: "",
			targetWinnings: "",
			winAmount: {
				min: "",
				max: "",
			},
			betInterval: {
				min: "",
				max: "",
			},
			pauseDuration: {
				min: "",
				max: "",
			},
			pausesPerDay: {
				min: "",
				max: "",
			},
			startDelayMinutes: {
				min: "",
				max: "",
			},
		};
	};

	getEmptyBotConfig = () => ({
		enabled: false,
		bots: [],
	});

	prepareBotConfig = (botConfig = this.props.tournamentsEditModal.config_bot) => {
		let preparedValue = botConfig;
		const isDailyRaceTournament = this.isDailyRaceTournament();

		if (typeof preparedValue === "string") {
			try {
				preparedValue = JSON.parse(preparedValue);
			} catch (error) {
				preparedValue = this.getEmptyBotConfig();
			}
		}

		if (!preparedValue || typeof preparedValue !== "object" || Array.isArray(preparedValue)) {
			preparedValue = this.getEmptyBotConfig();
		}

		const rawBots = Array.isArray(preparedValue.bots) ? preparedValue.bots.slice(0, MAX_TOURNAMENT_BOTS) : [];

		return {
			enabled: Boolean(preparedValue.enabled),
			bots: rawBots.map((bot, index) => {
				const emptyBot = this.getEmptyBot(index + 1, isDailyRaceTournament);
				const rawBot = bot && typeof bot === "object" ? bot : {};

				if (isDailyRaceTournament) {
					const rawTarget = rawBot.target && typeof rawBot.target === "object" ? rawBot.target : {};
					const fallbackTarget = typeof rawBot.targetBets === "undefined" ? "" : rawBot.targetBets;

					return {
						...emptyBot,
						slot: index + 1,
						// Selector assigns the bot user; it is kept so saving does not swap it mid-race.
						uid: rawBot.uid ?? null,
						target: {
							min: typeof rawTarget.min === "undefined" ? fallbackTarget : rawTarget.min,
							max: typeof rawTarget.max === "undefined" ? fallbackTarget : rawTarget.max,
						},
					};
				}

				return {
					...emptyBot,
					...rawBot,
					slot: index + 1,
					winAmount: {
						...emptyBot.winAmount,
						...(rawBot.winAmount && typeof rawBot.winAmount === "object" ? rawBot.winAmount : {}),
					},
					betInterval: {
						...emptyBot.betInterval,
						...(rawBot.betInterval && typeof rawBot.betInterval === "object" ? rawBot.betInterval : {}),
					},
					pauseDuration: {
						...emptyBot.pauseDuration,
						...(rawBot.pauseDuration && typeof rawBot.pauseDuration === "object" ? rawBot.pauseDuration : {}),
					},
					pausesPerDay: {
						...emptyBot.pausesPerDay,
						...(rawBot.pausesPerDay && typeof rawBot.pausesPerDay === "object" ? rawBot.pausesPerDay : {}),
					},
					startDelayMinutes: {
						...emptyBot.startDelayMinutes,
						...(rawBot.startDelayMinutes && typeof rawBot.startDelayMinutes === "object" ? rawBot.startDelayMinutes : {}),
					},
				};
			}),
		};
	};

	getDraftBotNumberValue = (value) =>
		value === "" || value === null || typeof value === "undefined" ? "" : Number(value);

	getDailyRaceDisplayTargetValue = (value) => {
		if (value === "" || value === null || typeof value === "undefined") {
			return value;
		}

		const numberValue = Number(value);
		return Number.isNaN(numberValue) ? value : numberValue / DAILY_RACE_TARGET_MULTIPLIER;
	};

	getWinningsDisplayValue = (value) => {
		if (value === "" || value === null || typeof value === "undefined") {
			return value;
		}

		const numberValue = Number(value);
		return Number.isNaN(numberValue) ? value : numberValue / WINNINGS_AMOUNT_MULTIPLIER;
	};

	toWinningsStoredValue = (value) => Math.round(Number(value) * WINNINGS_AMOUNT_MULTIPLIER);

	prepareWinningsBotConfigForDisplay = (botConfig) => {
		let preparedValue = botConfig;
		if (typeof preparedValue === "string") {
			try {
				preparedValue = JSON.parse(preparedValue);
			} catch (error) {
				return botConfig;
			}
		}

		if (!preparedValue || typeof preparedValue !== "object" || !Array.isArray(preparedValue.bots)) {
			return botConfig;
		}

		return {
			...preparedValue,
			bots: preparedValue.bots.map((bot) => {
				if (!bot || typeof bot !== "object") {
					return bot;
				}

				return {
					...bot,
					...(typeof bot.targetWinnings !== "undefined" ? { targetWinnings: this.getWinningsDisplayValue(bot.targetWinnings) } : {}),
					...(bot.winAmount && typeof bot.winAmount === "object"
						? {
								winAmount: {
									...bot.winAmount,
									min: this.getWinningsDisplayValue(bot.winAmount.min),
									max: this.getWinningsDisplayValue(bot.winAmount.max),
								},
							}
						: {}),
				};
			}),
		};
	};

	prepareBotConfigForDisplay = (botConfig = this.props.tournamentsEditModal.config_bot) => {
		if (!this.isDailyRaceTournament()) {
			return this.prepareWinningsBotConfigForDisplay(botConfig);
		}

		let preparedValue = botConfig;
		if (typeof preparedValue === "string") {
			try {
				preparedValue = JSON.parse(preparedValue);
			} catch (error) {
				return botConfig;
			}
		}

		if (!preparedValue || typeof preparedValue !== "object" || Array.isArray(preparedValue)) {
			return botConfig;
		}

		return {
			...preparedValue,
			bots: Array.isArray(preparedValue.bots)
				? preparedValue.bots.map((bot) => {
					if (!bot || typeof bot !== "object" || !bot.target || typeof bot.target !== "object") {
						return bot;
					}

					return {
						...bot,
						target: {
							...bot.target,
							min: this.getDailyRaceDisplayTargetValue(bot.target.min),
							max: this.getDailyRaceDisplayTargetValue(bot.target.max),
						},
					};
				})
				: preparedValue.bots,
		};
	};

	buildBotConfigDraft = (botConfig = this.props.tournamentsEditModal.config_bot) => {
		const preparedConfig = this.prepareBotConfig(botConfig);
		const isDailyRaceTournament = this.isDailyRaceTournament();

		if (isDailyRaceTournament) {
			return JSON.stringify(
				{
					enabled: preparedConfig.enabled,
					bots: preparedConfig.bots.map((bot, index) => ({
						slot: index + 1,
						uid: null,
						target: {
							min: this.getDraftBotNumberValue(bot.target.min),
							max: this.getDraftBotNumberValue(bot.target.max),
						},
					})),
				},
				null,
				2
			);
		}

		const isSumOfWinnings = this.isSumOfWinningsTournament();

		return JSON.stringify(
			{
				enabled: preparedConfig.enabled,
				bots: preparedConfig.bots.map((bot, index) => ({
					slot: index + 1,
					...(isSumOfWinnings
						? {
								targetWinnings: this.getDraftBotNumberValue(bot.targetWinnings),
								winAmount: {
									min: this.getDraftBotNumberValue(bot.winAmount.min),
									max: this.getDraftBotNumberValue(bot.winAmount.max),
								},
							}
						: { targetBets: this.getDraftBotNumberValue(bot.targetBets) }),
					betInterval: {
						min: this.getDraftBotNumberValue(bot.betInterval.min),
						max: this.getDraftBotNumberValue(bot.betInterval.max),
					},
					pauseDuration: {
						min: this.getDraftBotNumberValue(bot.pauseDuration.min),
						max: this.getDraftBotNumberValue(bot.pauseDuration.max),
					},
					pausesPerDay: {
						min: this.getDraftBotNumberValue(bot.pausesPerDay.min),
						max: this.getDraftBotNumberValue(bot.pausesPerDay.max),
					},
					startDelayMinutes: {
						min: this.getDraftBotNumberValue(bot.startDelayMinutes.min),
						max: this.getDraftBotNumberValue(bot.startDelayMinutes.max),
					},
				})),
			},
			null,
			2
		);
	};

	syncBotConfigDraft = (botConfig = this.props.tournamentsEditModal.config_bot) => {
		this.setState({
			botConfigDraft: this.buildBotConfigDraft(botConfig),
			botConfigError: "",
		});
	};

	getIntegerFieldError = (value, label, allowZero = true) => {
		if (value === "" || value === null || typeof value === "undefined") {
			return `${label} is required`;
		}

		const numberValue = Number(value);
		if (!Number.isInteger(numberValue) || numberValue < 0 || (!allowZero && numberValue === 0)) {
			return `${label} must be ${allowZero ? "a non-negative integer" : "a positive integer"}`;
		}

		return "";
	};

	getNumberFieldError = (value, label, allowZero = true) => {
		if (value === "" || value === null || typeof value === "undefined") {
			return `${label} is required`;
		}

		const numberValue = Number(value);
		if (Number.isNaN(numberValue) || numberValue < 0 || (!allowZero && numberValue === 0)) {
			return `${label} must be ${allowZero ? "a non-negative number" : "a positive number"}`;
		}

		return "";
	};

	getRangeFieldError = (range, label, allowDecimal = false) => {
		const minError = allowDecimal
			? this.getNumberFieldError(range.min, `${label} min`)
			: this.getIntegerFieldError(range.min, `${label} min`);
		if (minError) {
			return minError;
		}

		const maxError = allowDecimal
			? this.getNumberFieldError(range.max, `${label} max`)
			: this.getIntegerFieldError(range.max, `${label} max`);
		if (maxError) {
			return maxError;
		}

		if (Number(range.min) > Number(range.max)) {
			return `${label} min cannot exceed max`;
		}

		return "";
	};

	validateBotConfig = (botConfig = this.props.tournamentsEditModal.config_bot) => {
		const preparedConfig = this.prepareBotConfig(botConfig);
		const isDailyRaceTournament = this.isDailyRaceTournament();

		if (preparedConfig.bots.length > MAX_TOURNAMENT_BOTS) {
			return `Bot config cannot contain more than ${MAX_TOURNAMENT_BOTS} bots`;
		}

		if (preparedConfig.enabled && preparedConfig.bots.length === 0) {
			return "Add at least one bot or disable bot config";
		}

		if (!preparedConfig.enabled && preparedConfig.bots.length === 0) {
			return "";
		}

		for (let index = 0; index < preparedConfig.bots.length; index += 1) {
			const bot = preparedConfig.bots[index];
			const prefix = `Bot #${index + 1}`;

			if (isDailyRaceTournament) {
				const targetError = this.getRangeFieldError(bot.target, `${prefix} target`);
				if (targetError) {
					return targetError;
				}

				continue;
			}

			if (this.isSumOfWinningsTournament()) {
				const targetWinningsError = this.getNumberFieldError(bot.targetWinnings, `${prefix} targetWinnings`, false);
				if (targetWinningsError) {
					return targetWinningsError;
				}

				const winAmountError = this.getRangeFieldError(bot.winAmount, `${prefix} winAmount`, true);
				if (winAmountError) {
					return winAmountError;
				}

				if (Number(bot.winAmount.min) <= 0) {
					return `${prefix} winAmount min must be a positive number`;
				}
			} else {
				const targetBetsError = this.getIntegerFieldError(bot.targetBets, `${prefix} targetBets`);
				if (targetBetsError) {
					return targetBetsError;
				}
			}

			const betIntervalError = this.getRangeFieldError(bot.betInterval, `${prefix} betInterval`, true);
			if (betIntervalError) {
				return betIntervalError;
			}

			const pauseDurationError = this.getRangeFieldError(bot.pauseDuration, `${prefix} pauseDuration`);
			if (pauseDurationError) {
				return pauseDurationError;
			}

			const pausesPerDayError = this.getRangeFieldError(bot.pausesPerDay, `${prefix} pausesPerDay`);
			if (pausesPerDayError) {
				return pausesPerDayError;
			}

			const startDelayMinutesError = this.getRangeFieldError(bot.startDelayMinutes, `${prefix} startDelayMinutes`);
			if (startDelayMinutesError) {
				return startDelayMinutesError;
			}
		}

		return "";
	};

	prepareBotConfigForSubmit = (botConfig = this.props.tournamentsEditModal.config_bot) => {
		const preparedConfig = this.prepareBotConfig(botConfig);
		const isDailyRaceTournament = this.isDailyRaceTournament();

		if (isDailyRaceTournament) {
			return {
				enabled: preparedConfig.enabled,
				bots: preparedConfig.bots.map((bot, index) => ({
					slot: index + 1,
					uid: bot.uid ?? null,
					target: {
						min: Number(bot.target.min) * DAILY_RACE_TARGET_MULTIPLIER,
						max: Number(bot.target.max) * DAILY_RACE_TARGET_MULTIPLIER,
					},
				})),
			};
		}

		const isSumOfWinnings = this.isSumOfWinningsTournament();

		return {
			enabled: preparedConfig.enabled,
			bots: preparedConfig.bots.map(({ targetBets, targetWinnings, winAmount, ...bot }, index) => ({
				...bot,
				slot: index + 1,
				uid: null,
				...(isSumOfWinnings
					? {
							targetWinnings: this.toWinningsStoredValue(targetWinnings),
							winAmount: {
								min: this.toWinningsStoredValue(winAmount.min),
								max: this.toWinningsStoredValue(winAmount.max),
							},
						}
					: { targetBets: Number(targetBets) }),
				betInterval: {
					min: Number(bot.betInterval.min),
					max: Number(bot.betInterval.max),
				},
				pauseDuration: {
					min: Number(bot.pauseDuration.min),
					max: Number(bot.pauseDuration.max),
				},
				pausesPerDay: {
					min: Number(bot.pausesPerDay.min),
					max: Number(bot.pausesPerDay.max),
				},
				startDelayMinutes: {
					min: Number(bot.startDelayMinutes.min),
					max: Number(bot.startDelayMinutes.max),
				},
			})),
		};
	};

	updateBotConfigState = (nextConfig) => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		const preparedConfig = this.prepareBotConfig(nextConfig);

		setTournamentsEditModal({
			...tournamentsEditModal,
			config_bot: preparedConfig,
		});
		this.syncBotConfigDraft(preparedConfig);
	};

	handleBotConfigToggle = (enabled) => {
		const preparedConfig = this.prepareBotConfig();
		this.updateBotConfigState({
			...preparedConfig,
			enabled,
		});
	};

	addBotConfigBot = () => {
		const { Interface } = this.props;
		const preparedConfig = this.prepareBotConfig();

		if (preparedConfig.bots.length >= MAX_TOURNAMENT_BOTS) {
			Interface.addNoty("Error", `A maximum of ${MAX_TOURNAMENT_BOTS} bots is allowed`);
			return;
		}

		this.updateBotConfigState({
			...preparedConfig,
			bots: [...preparedConfig.bots, this.getEmptyBot(preparedConfig.bots.length + 1)],
		});
	};

	removeBotConfigBot = (index) => {
		const preparedConfig = this.prepareBotConfig();
		this.updateBotConfigState({
			...preparedConfig,
			bots: preparedConfig.bots.filter((_, botIndex) => botIndex !== index),
		});
	};

	updateBotConfigBotField = (index, field, value, nestedField = null) => {
		const preparedConfig = cloneDeep(this.prepareBotConfig());

		if (!preparedConfig.bots[index]) {
			return;
		}

		if (nestedField) {
			preparedConfig.bots[index][nestedField][field] = value;
		} else {
			preparedConfig.bots[index][field] = value;
		}

		this.updateBotConfigState(preparedConfig);
	};

	handleBotConfigTabChange = ({ detail }) => {
		if (detail.activeTabId === "json") {
			this.syncBotConfigDraft();
		}

		this.setState({
			botConfigTabId: detail.activeTabId,
			botConfigError: "",
		});
	};

	handleBotConfigDraftChange = (value) => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		this.setState({
			botConfigDraft: value,
		});

		try {
			const preparedConfig = this.parseBotConfigInput(value);
			setTournamentsEditModal({
				...tournamentsEditModal,
				config_bot: preparedConfig,
			});
			this.setState({
				botConfigError: "",
			});
		} catch (error) {
			this.setState({
				botConfigError: error.message,
			});
		}
	};

	copyBotConfigSelection = async () => {
		const { tournamentsEditModal, Interface } = this.props;
		const value = this.buildBotConfigDraft(tournamentsEditModal.config_bot);

		try {
			await this.copyTextToClipboard(value);
			Interface.addNoty("Success", "Copied bot config JSON");
		} catch (error) {
			Interface.addNoty("Error", "Failed to copy bot config");
		}
	};

	parseBotConfigInput = (value) => {
		const trimmedValue = value.trim();

		if (!trimmedValue) {
			throw new Error("Input is empty");
		}

		let parsedValue;
		try {
			parsedValue = JSON.parse(trimmedValue);
		} catch (error) {
			throw new Error(`Invalid JSON format: ${error.message}`);
		}

		if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
			throw new Error("JSON must be an object with enabled and bots fields");
		}

		const preparedConfig = this.prepareBotConfig(parsedValue);
		const validationError = this.validateBotConfig(preparedConfig);

		if (validationError) {
			throw new Error(validationError);
		}

		return preparedConfig;
	};

	getValidImportedIds = (rawIds, fieldName) => {
		if (!Array.isArray(rawIds)) {
			throw new Error(`${fieldName} must be an array of IDs`);
		}

		const importedIds = rawIds.map((rawId) => {
			const preparedId =
				typeof rawId === "number"
					? rawId
					: typeof rawId === "string" && rawId.trim() !== ""
						? Number(rawId.trim())
						: NaN;

			if (!Number.isInteger(preparedId) || preparedId <= 0) {
				throw new Error(`${fieldName} must contain positive integer IDs only`);
			}

			return preparedId.toString();
		});
		const duplicateIds = [...new Set(importedIds.filter((id, index) => importedIds.indexOf(id) !== index))];

		if (duplicateIds.length > 0) {
			throw new Error(`${fieldName} contains duplicate IDs: ${duplicateIds.join(", ")}`);
		}

		return importedIds;
	};

	parseGameProviderInput = (value) => {
		const trimmedValue = value.trim();

		if (!trimmedValue) {
			throw new Error("Input is empty");
		}

		let providerIdsValue;
		let gameIdsValue;

		if (trimmedValue.startsWith("{") || trimmedValue.startsWith("[")) {
			let parsedValue;

			try {
				parsedValue = JSON.parse(trimmedValue);
			} catch (error) {
				throw new Error(`Invalid JSON format: ${error.message}`);
			}

			if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
				throw new Error("JSON must be an object with provider_ids and game_ids arrays");
			}

			providerIdsValue = parsedValue[PROVIDER_IDS_KEY];
			gameIdsValue = parsedValue[GAME_IDS_KEY];
		} else {
			const rows = trimmedValue
				.split(/\r?\n/)
				.map((line) => line.trim())
				.filter(Boolean);
			const parsedRows = {};

			if (rows.length === 0) {
				throw new Error("CSV input is empty");
			}

			rows.forEach((row) => {
				const columns = row.split(",").map((column) => column.trim());
				const rowKey = columns[0];

				if (rowKey !== PROVIDER_IDS_KEY && rowKey !== GAME_IDS_KEY) {
					throw new Error("CSV rows must start with provider_ids or game_ids");
				}

				if (rowKey in parsedRows) {
					throw new Error(`CSV contains duplicate '${rowKey}' rows`);
				}

				parsedRows[rowKey] = columns.slice(1).filter((column) => column !== "");
			});

			if (!(PROVIDER_IDS_KEY in parsedRows) || !(GAME_IDS_KEY in parsedRows)) {
				throw new Error("CSV must contain both 'provider_ids' and 'game_ids' rows");
			}

			providerIdsValue = parsedRows[PROVIDER_IDS_KEY];
			gameIdsValue = parsedRows[GAME_IDS_KEY];
		}

		const providerIds = this.getValidImportedIds(providerIdsValue, PROVIDER_IDS_KEY);
		const gameIds = this.getValidImportedIds(gameIdsValue, GAME_IDS_KEY);
		const { game } = this.props;
		const providerMap = new Map(game.providers.map((provider) => [provider.id.toString(), provider]));
		const gameMap = new Map(game.data.map((gameItem) => [gameItem.gameID.toString(), gameItem]));
		const unknownProviderIds = providerIds.filter((providerId) => !providerMap.has(providerId));
		const unknownGameIds = gameIds.filter((gameId) => !gameMap.has(gameId));

		if (unknownProviderIds.length > 0) {
			throw new Error(`Unknown provider IDs: ${unknownProviderIds.join(", ")}`);
		}

		if (unknownGameIds.length > 0) {
			throw new Error(`Unknown game IDs: ${unknownGameIds.join(", ")}`);
		}

		const providerIdSet = new Set(providerIds);
		const wrongGameIds = gameIds.filter((gameId) => {
			const gameItem = gameMap.get(gameId);
			return !providerIdSet.has(gameItem.providerID.toString());
		});

		if (wrongGameIds.length > 0) {
			throw new Error(`These games do not belong to the selected providers: ${wrongGameIds.join(", ")}`);
		}

		return {
			providerIds,
			gameIds,
			providerOptions: providerIds.map((providerId) => this.getProviderOption(providerMap.get(providerId))),
			gameOptions: gameIds.map((gameId) => this.getGameOption(gameMap.get(gameId))),
		};
	};

	syncGameProviderDraft = (providerSelection = this.props.tournamentsEditModal.provider_ids, gameSelection = this.props.tournamentsEditModal.game_ids) => {
		this.setState({
			gameProviderDraft: this.buildGameProviderDraft(providerSelection, gameSelection),
			gameProviderError: "",
		});
	};

	copyTextToClipboard = async (value) => {
		if (navigator.clipboard && navigator.clipboard.writeText) {
			await navigator.clipboard.writeText(value);
			return;
		}

		const textarea = document.createElement("textarea");
		textarea.value = value;
		textarea.setAttribute("readonly", "");
		textarea.style.position = "absolute";
		textarea.style.left = "-9999px";
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand("copy");
		document.body.removeChild(textarea);
	};

	copyGameProviderSelection = async () => {
		const { tournamentsEditModal, Interface } = this.props;
		const value = this.buildGameProviderDraft(
			tournamentsEditModal.provider_ids,
			tournamentsEditModal.game_ids
		);

		try {
			await this.copyTextToClipboard(value);
			Interface.addNoty("Success", "Copied JSON set");
		} catch (error) {
			Interface.addNoty("Error", "Failed to copy selection");
		}
	};

	handleGameProviderTabChange = ({ detail }) => {
		if (detail.activeTabId === "json") {
			this.syncGameProviderDraft();
		}

		this.setState({
			gameProviderTabId: detail.activeTabId,
			gameProviderError: "",
		});
	};

	handleGameProviderDraftChange = (value) => {
		this.setState({
			gameProviderDraft: value,
			gameProviderError: "",
		});
	};

	applyGameProviderSelection = () => {
		const { game, tournamentsEditModal, setTournamentsEditModal } = this.props;

		try {
			const parsedSet = this.parseGameProviderInput(this.state.gameProviderDraft);
			const nextGames = this.getGameOptionsByProviders(parsedSet.providerIds);

			setTournamentsEditModal({
				...tournamentsEditModal,
				provider_ids: parsedSet.providerOptions,
				game_ids: parsedSet.gameOptions,
			});

			this.setState({
				games: nextGames,
				filteredGames: nextGames.slice(0, 30),
				gameProviderDraft: this.buildGameProviderDraft(
					parsedSet.providerOptions,
					parsedSet.gameOptions
				),
				gameProviderError: "",
			});
		} catch (error) {
			this.setState({
				gameProviderError: error.message,
			});
		}
	};

	componentDidMount() {
		const { tournamentsEditModal, setTournamentsEditModal, game } = this.props;
		const preparedProviders = this.prepareProviderSelection(
			tournamentsEditModal.provider_ids,
			game.providers
		);
		const preparedGames = this.prepareGameSelection(
			tournamentsEditModal.game_ids,
			game.data
		);
		const nextGames = this.getGameOptionsByProviders(preparedProviders);
		const preparedBotConfig = this.prepareBotConfig(
			this.prepareBotConfigForDisplay(tournamentsEditModal.config_bot)
		);

		// Инициализация JSON-представления
		this.setState({
			prizeDistributionJson: JSON.stringify(tournamentsEditModal.prizeDistribution, null, 2),
			jsonError: "",
			games: nextGames,
			filteredGames: nextGames.slice(0, 30),
			gameProviderDraft: this.buildGameProviderDraft(preparedProviders, preparedGames),
			gameProviderError: "",
			gameProviderTabId: "regular",
			botConfigDraft: this.buildBotConfigDraft(preparedBotConfig),
			botConfigError: "",
			botConfigTabId: "regular",
		});

		setTournamentsEditModal({
			...tournamentsEditModal,
			game_ids: preparedGames,
			provider_ids: preparedProviders,
			config_bot: preparedBotConfig,
		});
	}

	updateTournament = async () => {
		try {
			const { tournamentsEditModal, Interface, setTournamentsEditModal } = this.props;

			// Валидация
			if (tournamentsEditModal.name === "") {
				Interface.addNoty("Error", "Tournament name is required");
				return;
			}
			if (tournamentsEditModal.slug === "") {
				Interface.addNoty("Error", "Slug is required");
				return;
			}
			if (tournamentsEditModal.slug && !/^[a-z0-9_]{1,30}$/.test(tournamentsEditModal.slug)) {
				Interface.addNoty("Error", "Slug must be 1-30 latin characters, numbers or underscore");
				return;
			}
			if (tournamentsEditModal.tournamentType === "") {
				Interface.addNoty("Error", "Tournament type is required");
				return;
			}
			const isDailyRace = this.isDailyRaceTournament(tournamentsEditModal);
			const hasDailyRacePrizes = this.hasDailyRacePrizes(tournamentsEditModal);

			if (hasDailyRacePrizes) {
				const dailyRacePrizesError = this.getDailyRacePrizesError();
				if (dailyRacePrizesError) {
					Interface.addNoty("Error", dailyRacePrizesError);
					return;
				}
			} else if (!isDailyRace) {
				if (this.state.jsonError) {
					Interface.addNoty("Error", "Invalid JSON in Prize Distribution");
					return;
				}

				const totalDistributed = tournamentsEditModal.prizeDistribution.reduce(
					(sum, item) => sum + Number(item.prize || 0),
					0
				);
				const prizePool = Number(tournamentsEditModal.prize_pool) || 0;
				if (totalDistributed !== prizePool && tournamentsEditModal.prizeDistribution.length > 0) {
					Interface.addNoty("Error", "Prize pool doesn't match the distributed total. Please check the distribution.");
					return;
				}
			}

			if (this.state.botConfigError) {
				Interface.addNoty("Error", this.state.botConfigError);
				return;
			}

			const botConfigError = this.validateBotConfig(tournamentsEditModal.config_bot);
			if (botConfigError) {
				Interface.addNoty("Error", botConfigError);
				return;
			}

			this.setState({ loading: true });
			const normalizedBotConfig = this.prepareBotConfigForSubmit(tournamentsEditModal.config_bot);

			const body = {
				...tournamentsEditModal,
				games: tournamentsEditModal.game_ids.map(element => parseInt(element.value)),
				providers: tournamentsEditModal.provider_ids.map(element => parseInt(element.value)),
				type: tournamentsEditModal.modalType,
				from: tournamentsEditModal.from,
				to: tournamentsEditModal.to,
				// Daily race amounts go as typed ("1234,56"); Core converts them to kopecks.
				prizeDistribution: hasDailyRacePrizes
					? createDailyRacePrizeRows(tournamentsEditModal.prizeDistribution)
					: isDailyRace
						? undefined
						: tournamentsEditModal.prizeDistribution.map(item => ({
							...item,
							prize: Number(item.prize),
						})),
				min_bet: tournamentsEditModal.min_bet === "" ? null : Number(tournamentsEditModal.min_bet),
				prize_pool: hasDailyRacePrizes ? tournamentsEditModal.prize_pool : Number(tournamentsEditModal.prize_pool),
				deposit_count: tournamentsEditModal.deposit_count ? Number(tournamentsEditModal.deposit_count) : "0",
				vip_level: tournamentsEditModal.vip_level ? Number(tournamentsEditModal.vip_level) : null,
				status: tournamentsEditModal.status.value,
				config_bot: normalizedBotConfig,
				bot_config: normalizedBotConfig,
			};

			try {
				const result = await Interface.request("set", {
					class: "data",
					module: "tournaments",
					method: "edit",
				}, body);

				Interface.addNoty("Success", "Tournament was successfully updated");
				setTournamentsEditModal({ ...tournamentsEditModal, show: false });
				Interface.loadTournamentList(undefined, tournamentsEditModal.modalType);
			} catch (error) {
				Interface.addNoty("Error", error.text || error.toString());
			} finally {
				this.setState({ loading: false });
			}
		} catch (error) {
			this.setState({ loading: false });
		}
	};

	// Prizes of the daily race are edited on the race itself (not on the template).
	hasDailyRacePrizes = (tournament = this.props.tournamentsEditModal) =>
		this.isDailyRaceTournament(tournament) && tournament.modalType === "tournaments";

	getDailyRacePrizeState = (tournament = this.props.tournamentsEditModal) => {
		const poolKopecks = parseRubToKopecks(tournament.prize_pool);
		const rows = createDailyRacePrizeRows(tournament.prizeDistribution);
		const prizes = rows.map((row) => (row.prize === "" ? null : parseRubToKopecks(row.prize)));

		return {
			distributed: prizes.reduce((sum, prize) => sum + (prize || 0), 0),
			emptyCount: rows.filter((row) => row.prize === "").length,
			invalidPlaces: rows.filter((row, index) => row.prize !== "" && prizes[index] === null).map((row) => row.place),
			poolKopecks,
			prizes,
			rows,
		};
	};

	getDailyRacePrizesError = () => {
		const { distributed, emptyCount, invalidPlaces, poolKopecks } = this.getDailyRacePrizeState();

		if (!poolKopecks) return "Enter a valid prize pool, e.g. 10000 or 10000,50";
		if (invalidPlaces.length) return `Invalid prize at place ${invalidPlaces.slice(0, 5).join(", ")}${invalidPlaces.length > 5 ? "…" : ""}`;
		if (emptyCount) return `Fill prizes for all ${DAILY_RACE_PLACES} places (${emptyCount} empty)`;
		if (distributed > poolKopecks) return `Prizes total exceeds the prize pool by ${formatKopecks(distributed - poolKopecks)}`;

		return "";
	};

	setDailyRacePrizes = (prizes) => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;

		setTournamentsEditModal({
			...tournamentsEditModal,
			prizeDistribution: prizes.map((prize, index) => ({ place: index + 1, prize: prize === null ? "" : formatKopecksInput(prize) })),
		});
	};

	updateDailyRacePrize = (index, value) => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		const rows = createDailyRacePrizeRows(tournamentsEditModal.prizeDistribution);

		rows[index] = { ...rows[index], prize: value };
		setTournamentsEditModal({ ...tournamentsEditModal, prizeDistribution: rows });
	};

	// Fills the form only; nothing is saved until Save.
	distributeDailyRacePrizes = (onlyEmpty) => {
		const { Interface } = this.props;
		const { invalidPlaces, poolKopecks, prizes } = this.getDailyRacePrizeState();

		if (!poolKopecks) {
			Interface.addNoty("Error", "Enter a valid prize pool first");
			return;
		}

		if (!onlyEmpty) {
			this.setDailyRacePrizes(distributeAllPrizes(poolKopecks));
			return;
		}

		if (invalidPlaces.length) {
			Interface.addNoty("Error", `Fix the prize at place ${invalidPlaces[0]} first`);
			return;
		}
		if (!prizes.includes(null)) {
			Interface.addNoty("Error", "There are no empty places");
			return;
		}

		const result = distributeEmptyPrizes(poolKopecks, prizes);
		if (result.remainder <= 0) {
			Interface.addNoty("Error", "Nothing is left of the prize pool for the empty places");
			return;
		}

		this.setDailyRacePrizes(result.prizes);
	};

	renderDailyRacePrizes = () => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		const { distributed, poolKopecks, rows } = this.getDailyRacePrizeState();
		const pool = poolKopecks || 0;
		const left = pool - distributed;
		const error = this.getDailyRacePrizesError();

		return (
			<Container header={<Box variant="h2">{`Prize Pool: distributed ${formatKopecks(distributed)} of ${formatKopecks(pool)}`}</Box>}>
				<SpaceBetween direction="vertical" size="m">
					<ColumnLayout columns={2}>
						<FormField
							label="Prize Pool (RUB)"
							description="Kopecks go after a comma, e.g. 10000,50"
							errorText={tournamentsEditModal.prize_pool !== "" && !poolKopecks ? "Invalid amount" : ""}
							stretch
						>
							<Input
								value={tournamentsEditModal.prize_pool}
								inputMode="decimal"
								onChange={({ detail }) => setTournamentsEditModal({ ...tournamentsEditModal, prize_pool: detail.value })}
								placeholder="10000"
							/>
						</FormField>
						<FormField label="Number of Winners" description={`The daily race always pays ${DAILY_RACE_PLACES} places`} stretch>
							<Input value={String(DAILY_RACE_PLACES)} disabled />
						</FormField>
					</ColumnLayout>
					<Box variant="strong" color={left < 0 ? "text-status-error" : "text-body-secondary"}>
						{left < 0 ? `Over the prize pool by ${formatKopecks(-left)}` : `Left to distribute: ${formatKopecks(left)}`}
					</Box>
					<SpaceBetween direction="horizontal" size="xs">
						<Button onClick={() => this.distributeDailyRacePrizes(false)}>Distribute prizes by script</Button>
						<Button onClick={() => this.distributeDailyRacePrizes(true)}>Distribute empty places by script</Button>
					</SpaceBetween>
					<Box color="text-body-secondary">
						The script uses the current automatic formula, rounded to 10 RUB. Nothing is saved until you press Save. Saved prizes apply to today's race and carry over to the next days; until then the race keeps the automatic distribution.
					</Box>
					{error ? <Box color="text-status-error">{error}</Box> : null}
					<div style={{ maxHeight: 480, overflowY: "auto" }}>
						<Table
							variant="embedded"
							items={rows}
							trackBy="place"
							columnDefinitions={[
								{ id: "place", header: "Place", cell: (row) => row.place, width: 100 },
								{
									id: "prize",
									header: "Prize (RUB)",
									cell: (row) => (
										<Input
											value={row.prize}
											inputMode="decimal"
											invalid={row.prize !== "" && parseRubToKopecks(row.prize) === null}
											onChange={({ detail }) => this.updateDailyRacePrize(row.place - 1, detail.value)}
											placeholder="0"
										/>
									),
								},
							]}
						/>
					</div>
				</SpaceBetween>
			</Container>
		);
	};

	updateParam = (key, value) => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		const updatedState = cloneDeep(tournamentsEditModal);
		updatedState[key] = value;
		setTournamentsEditModal(updatedState);

		if (key === "winners") {
			setTimeout(() => {
				this.updatePrizeDistributionTable(Number(value));
			}, 0);
		}
		if (key === "prize_pool" && tournamentsEditModal.distributionMethod === "even") {
			setTimeout(() => {
				this.updatePrizeDistributionTable(Number(tournamentsEditModal.winners));
			}, 0);
		}
	};

	filterGamesByProviders(selectedProviders) {
		const options = this.getGameOptionsByProviders(selectedProviders);

		this.setState({
			games: options,
			filteredGames: options.slice(0, 30),
		});
	}

	handleProviderChange(selectedProviders) {
		this.props.setTournamentsEditModal({
			...this.props.tournamentsEditModal,
			provider_ids: selectedProviders,
			game_ids: selectedProviders.length === 0 ? [] : this.props.tournamentsEditModal.game_ids,
		});
		this.filterGamesByProviders(selectedProviders.map(provider => provider.value));
	}

	handleGameSearch(filteringText) {
		const filteredGames = this.state.games.filter(game =>
			game.label.toLowerCase().includes(filteringText.toLowerCase())
		);
		this.setState({ filteredGames: filteredGames.slice(0, 30) });
	}

	handleProviderSearch(filteringText) {
		const { game } = this.props;
		const filterProvider = game.providers.filter(provider =>
			provider.name.toLowerCase().includes(filteringText.toLowerCase())
		);
		this.setState({ filterProvider: filterProvider });
	}

	updatePrizeDistribution = (index, field, value) => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		const newDistribution = [...tournamentsEditModal.prizeDistribution];
		newDistribution[index] = { ...newDistribution[index], [field]: value };
		setTournamentsEditModal({ ...tournamentsEditModal, prizeDistribution: newDistribution });

		// Обновляем JSON
		this.setState({
			prizeDistributionJson: JSON.stringify(newDistribution, null, 2),
			jsonError: "",
		});
	};

	updatePrizeDistributionTable = (winnersCount) => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		const { prize_pool, distributionMethod } = tournamentsEditModal;

		if (!winnersCount || winnersCount < 1) {
			const defaultDistribution = [{ place: 1, prize: "" }];
			setTournamentsEditModal({
				...tournamentsEditModal,
				prizeDistribution: defaultDistribution,
			});
			this.setState({
				prizeDistributionJson: JSON.stringify(defaultDistribution, null, 2),
				jsonError: "",
			});
			return;
		}

		let newDistribution = [];
		if (distributionMethod === "even") {
			newDistribution = Array.from({ length: winnersCount }, (_, index) => ({
				place: index + 1,
				prize: prize_pool ? Math.floor(Number(prize_pool) / winnersCount).toString() : "",
			}));
		} else if (distributionMethod === "competition") {
			const totalPrizePool = Number(prize_pool) || 0;
			if (totalPrizePool === 0) {
				newDistribution = Array.from({ length: winnersCount }, (_, index) => ({
					place: index + 1,
					prize: "",
				}));
			} else {
				const topPrizePool = totalPrizePool * 0.5;
				const remainingPrizePool = totalPrizePool * 0.5;
				const topPlaces = Math.min(winnersCount, 10);
				const remainingPlaces = winnersCount - topPlaces;

				const topPrizes = [];
				let remainingTopPrize = topPrizePool;
				for (let i = 1; i <= topPlaces; i++) {
					const weight = (topPlaces - i + 1) / (topPlaces * (topPlaces + 1) / 2);
					const prize = Math.floor(remainingTopPrize * weight);
					topPrizes.push(prize);
					remainingTopPrize -= prize;
				}

				const remainingPrizes = [];
				let remainingPrize = remainingPrizePool;
				for (let i = 1; i <= remainingPlaces; i++) {
					const weight = (remainingPlaces - i + 1) / (remainingPlaces * (remainingPlaces + 1) / 2);
					const prize = Math.floor(remainingPrize * weight);
					remainingPrizes.push(prize);
					remainingPrize -= prize;
				}

				newDistribution = Array.from({ length: winnersCount }, (_, index) => ({
					place: index + 1,
					prize: index < topPlaces
						? topPrizes[index].toString()
						: remainingPrizes[index - topPlaces].toString(),
				}));
			}
		} else {
			newDistribution = Array.from({ length: winnersCount }, (_, index) => ({
				place: index + 1,
				prize: "",
			}));
		}

		setTournamentsEditModal({
			...tournamentsEditModal,
			prizeDistribution: newDistribution,
			winners: winnersCount.toString(),
		});

		// Обновляем JSON
		this.setState({
			prizeDistributionJson: JSON.stringify(newDistribution, null, 2),
			jsonError: "",
		});
	};

	handleDistributionMethodChange = (method) => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		setTournamentsEditModal({ ...tournamentsEditModal, distributionMethod: method });
		setTimeout(() => {
			this.updatePrizeDistributionTable(Number(tournamentsEditModal.winners));
		}, 0);
	};

	addPrizeRow = () => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		const newDistribution = [...tournamentsEditModal.prizeDistribution];
		const lastPlace = newDistribution[newDistribution.length - 1].place;
		newDistribution.push({ place: lastPlace + 1, prize: "" });
		setTournamentsEditModal({
			...tournamentsEditModal,
			prizeDistribution: newDistribution,
			winners: newDistribution.length.toString(),
		});

		// Обновляем JSON
		this.setState({
			prizeDistributionJson: JSON.stringify(newDistribution, null, 2),
			jsonError: "",
		});
	};

	removePrizeRow = (index) => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		const newDistribution = tournamentsEditModal.prizeDistribution.filter((_, i) => i !== index);
		setTournamentsEditModal({
			...tournamentsEditModal,
			prizeDistribution: newDistribution.map((item, i) => ({ ...item, place: i + 1 })),
			winners: newDistribution.length.toString(),
		});

		// Обновляем JSON
		this.setState({
			prizeDistributionJson: JSON.stringify(newDistribution, null, 2),
			jsonError: "",
		});
	};

	handleJsonChange = (jsonString) => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		this.setState({ prizeDistributionJson: jsonString });

		try {
			const parsedJson = JSON.parse(jsonString);
			if (!Array.isArray(parsedJson)) {
				this.setState({ jsonError: "JSON must be an array of objects" });
				return;
			}

			// Валидация структуры
			const isValid = parsedJson.every((item, index) => {
				return (
					item &&
					typeof item === "object" &&
					"place" in item &&
					"prize" in item &&
					Number.isInteger(item.place) &&
					item.place === index + 1 &&
					(typeof item.prize === "number" || Number.isInteger(item.place))
				);
			});

			if (!isValid) {
				this.setState({
					jsonError:
						"Invalid JSON structure. Each item must have 'place' (integer, sequential) and 'prize' (number).",
				});
				return;
			}

			// Обновляем prizeDistribution
			setTournamentsEditModal({
				...tournamentsEditModal,
				prizeDistribution: parsedJson,
				winners: parsedJson.length.toString(),
			});
			this.setState({ jsonError: "" });
		} catch (error) {
			this.setState({ jsonError: "Invalid JSON format: " + error.message });
		}
	};

	checkPrizeDistribution = () => {
		const { tournamentsEditModal, setTournamentsEditModal } = this.props;
		const totalDistributed = tournamentsEditModal.prizeDistribution.reduce(
			(sum, item) => sum + Number(item.prize || 0),
			0
		);
		const prizePool = Number(tournamentsEditModal.prize_pool) || 0;
		const isDistributionValid = totalDistributed === prizePool;

		setTournamentsEditModal({ ...tournamentsEditModal, showPrizeValidationAlert: !isDistributionValid });
	};

	render() {
		const { tournamentsEditModal, setTournamentsEditModal, game } = this.props;

		const totalDistributed = tournamentsEditModal.prizeDistribution.reduce(
			(sum, item) => sum + Number(item.prize || 0),
			0
		);
		const prizePool = Number(tournamentsEditModal.prize_pool) || 0;
		const botConfig = this.prepareBotConfig(tournamentsEditModal.config_bot);
		const isDailyRaceTournament = this.isDailyRaceTournament(tournamentsEditModal);
		const tournamentTypeValue = tournamentsEditModal.tournamentType && tournamentsEditModal.tournamentType.value;
		const isSumOfWinningsTournament = this.isSumOfWinningsTournament(tournamentsEditModal);
		const shouldShowBotConfig = tournamentsEditModal.modalType === "tournaments" && (
			isDailyRaceTournament || tournamentTypeValue === "number_of_bets" || isSumOfWinningsTournament
		);

		return (
			<Modal
				visible={tournamentsEditModal.show}
				size="max"
				onDismiss={() => setTournamentsEditModal({ ...tournamentsEditModal, show: false })}
				header={`Edit Tournament ${tournamentsEditModal.modalType === "tournaments" ? "" : "recurrence"}`}
				children={
					<SpaceBetween direction="vertical" size="l">
						{/* Основные настройки */}
						<Container header={<Box variant="h2">Basic Settings</Box>}>
							<SpaceBetween direction="vertical" size="m">
								<ColumnLayout columns={2}>
									<FormField
										label="Name"
										description="Enter the name of the tournament"
										stretch
									>
										<Input
											value={tournamentsEditModal.name}
											onChange={({ detail }) => this.updateParam("name", detail.value)}
											placeholder="Tournament Name"
										/>
									</FormField>
									<FormField
										label="Slug"
										description="Unique URL slug (latin letters, numbers, underscore, max 30 characters)"
										stretch
									>
										<Input
											value={tournamentsEditModal.slug}
											onChange={({ detail }) => this.updateParam("slug", detail.value)}
											placeholder="tournament-slug"
										/>
									</FormField>
								</ColumnLayout>


								<FormField
									label="Translation type"
									description="Translation type must be international or local"
									stretch
								>
									<Select
										selectedOption={tournamentsEditModal.translationType ? {
											value: tournamentsEditModal.translationType,
											label: tournamentsEditModal.translationType,
										} : null}
										onChange={({ detail }) => this.updateParam("translationType", detail.selectedOption.value)}
										options={tournamentsEditModal.currentOption.translationType}
										placeholder="Select translationType"
									/>
								</FormField>

								{tournamentsEditModal.modalType === "tournaments" && (
									<FormField
										label="Minimum Bet (RUB)"
										description="Optional: Minimum bet to participate (1-2000 RUB)"
										stretch
									>
										<Input
											type="number"
											value={tournamentsEditModal.min_bet}
											onChange={({ detail }) => {
												const value = detail.value;
												this.updateParam(
													"min_bet",
													value === "" ? "" : Math.max(1, Math.min(2000, Number(value)))
												);
											}}
											placeholder="10 (optional)"
										/>
									</FormField>
								)}

								<SpaceBetween direction="vertical" size="m">
									{tournamentsEditModal.modalType !== "tournaments" && (
										<FormField
											label="Period Type"
											description="Select the period type for the tournament"
											stretch
										>
											<Select
												selectedOption={tournamentsEditModal.periodType}
												onChange={({ detail }) =>
													this.updateParam("periodType", detail.selectedOption)
												}
												options={tournamentsEditModal.currentOption.periodType}
												placeholder="Select period type"
											/>
										</FormField>
									)}
								</SpaceBetween>

								<FormField label="Status" description="Set the status of the tournament" stretch>
									<Select
										selectedOption={tournamentsEditModal.status}
										onChange={({ detail }) => this.updateParam("status", detail.selectedOption)}
										options={tournamentsEditModal.currentOption.statusType}
										placeholder="Select status"
									/>
								</FormField>

								{tournamentsEditModal.modalType === "tournaments" && (
									<FormField
										label="Banner"
										description="Upload a banner image for the tournament"
										stretch
									>
										<SpaceBetween direction="vertical" size="m">
											<input
												type="file"
												accept="image/png"
												onChange={(event) => {
													const file = event.target.files[0];
													if (file) {
														const reader = new FileReader();
														reader.onload = (event) => {
															const result = event.target.result;
															this.updateParam("banner", result);
														};
														reader.readAsDataURL(file);
													} else {
														this.props.Interface.addNoty(
															"Error",
															"Please, select a valid image file"
														);
													}
												}}
											/>
											<ProgressiveImage
												src={tournamentsEditModal.banner_url}
												placeholder={Poster}
											>
												{(src) => (
													<img
														style={{ width: "200px" }}
														src={src}
														alt={tournamentsEditModal.name}
													/>
												)}
											</ProgressiveImage>
										</SpaceBetween>
									</FormField>
								)}
							</SpaceBetween>
						</Container>

						{/* Игры и провайдеры */}
						{tournamentsEditModal.modalType === "tournaments" && (
							<Container header={<Box variant="h2">Games and Providers</Box>}>
								<Tabs
									activeTabId={this.state.gameProviderTabId}
									onChange={this.handleGameProviderTabChange}
									tabs={[
										{
											label: "Regular",
											id: "regular",
											content: (
												<SpaceBetween direction="vertical" size="m">
													<FormField
														label="Providers"
														description="Select providers for the tournament"
														stretch
													>
														<Multiselect
															filteringType="manual"
															selectedOptions={tournamentsEditModal.provider_ids}
															onLoadItems={({ detail }) =>
																this.handleProviderSearch(detail.filteringText)
															}
															onChange={({ detail }) =>
																this.handleProviderChange(detail.selectedOptions)
															}
															options={this.getProviderOptions(this.state.filterProvider)}
															disabled={game.providers.length === 0}
															placeholder="Select providers"
														/>
													</FormField>

													<FormField label="Games" description="Select games for the tournament" stretch>
														<Multiselect
															filteringType="manual"
															onLoadItems={({ detail }) => this.handleGameSearch(detail.filteringText)}
															selectedOptions={tournamentsEditModal.game_ids}
															onChange={({ detail }) =>
																this.updateParam("game_ids", detail.selectedOptions)
															}
															options={this.state.filteredGames}
															disabled={this.state.games.length === 0}
															placeholder="Select games"
														/>
													</FormField>
												</SpaceBetween>
											),
										},
										{
											label: "JSON",
											id: "json",
											content: (
												<SpaceBetween direction="vertical" size="m">
													<Box color="text-body-secondary">
														Paste a full provider/game set in JSON or CSV. Invalid input will not change the current selection.
													</Box>
													<SpaceBetween direction="horizontal" size="xs">
														<Button onClick={this.copyGameProviderSelection}>
															Copy JSON
														</Button>
														<Button variant="primary" onClick={this.applyGameProviderSelection}>
															Apply
														</Button>
													</SpaceBetween>
													<FormField
														label="Bulk input"
														description={'Supported formats: {"provider_ids":[1,2],"game_ids":[101,102]} or provider_ids,1,2 / game_ids,101,102'}
														errorText={this.state.gameProviderError}
														stretch
													>
														<Textarea
															value={this.state.gameProviderDraft}
															onChange={({ detail }) =>
																this.handleGameProviderDraftChange(detail.value)
															}
															placeholder={`{\n  "provider_ids": [1, 2],\n  "game_ids": [101, 102]\n}\n\nprovider_ids,1,2\ngame_ids,101,102`}
															rows={12}
														/>
													</FormField>
												</SpaceBetween>
											),
										},
									]}
								/>
							</Container>
						)}

						{/* Призовой фонд */}
						{this.hasDailyRacePrizes(tournamentsEditModal) ? this.renderDailyRacePrizes() : (
						<Container header={<Box variant="h2">Prize Pool</Box>}>
							<SpaceBetween direction="vertical" size="m">
								<ColumnLayout columns={2}>
									<FormField
										label="Prize Pool (RUB)"
										description="Total prize pool for the tournament"
										stretch
									>
										<Input
											type="number"
											value={tournamentsEditModal.prize_pool}
											onChange={({ detail }) => this.updateParam("prize_pool", detail.value)}
											placeholder="10000"
										/>
									</FormField>

									<FormField
										label="Number of Winners"
										description="Number of prize places"
										stretch
									>
										<Input
											type="number"
											value={tournamentsEditModal.winners}
											onChange={({ detail }) =>
												this.updateParam(
													"winners",
													detail.value === ""
														? ""
														: Math.max(0, Math.min(500, Number(detail.value)))
												)
											}
											placeholder="1"
											inputMode="numeric"
										/>
									</FormField>
								</ColumnLayout>

								{tournamentsEditModal.modalType === "tournaments" && (
									<FormField
										label="Prize Distribution"
										description={
											tournamentsEditModal.prizeDistribution.length > 0 ? (
												<Box
													color={
														totalDistributed !== prizePool
															? "text-status-error"
															: "text-status-success"
													}
													fontSize={"body-m"}
												>
													{`Total Distributed: ${totalDistributed} RUB / Prize Pool: ${prizePool} RUB`}
												</Box>
											) : (
												"Distribute the prize pool among places"
											)
										}
										errorText={this.state.jsonError}
										stretch
									>
										<Tabs
											tabs={[
												{
													label: "Table",
													id: "table",
													content: (
														<Table
															columnDefinitions={[
																{
																	id: "place",
																	header: "Place",
																	cell: item => item.place,
																	width: 100,
																},
																{
																	id: "prize",
																	header: "Prize (RUB)",
																	cell: item => (
																		<Input
																			type="number"
																			value={item.prize}
																			onChange={({ detail }) =>
																				this.updatePrizeDistribution(
																					item.place - 1,
																					"prize",
																					detail.value
																				)
																			}
																		/>
																	),
																},
																{
																	id: "actions",
																	cell: item => (
																		<Button
																			variant="inline-link"
																			onClick={() =>
																				this.removePrizeRow(item.place - 1)
																			}
																			disabled={
																				tournamentsEditModal.prizeDistribution
																					.length === 1
																			}
																		>
																			Remove
																		</Button>
																	),
																	width: 200,
																},
															]}
															items={tournamentsEditModal.prizeDistribution}
															footer={
																<Button
																	variant="link"
																	onClick={this.addPrizeRow}
																>
																	Add Place
																</Button>
															}
														/>
													),
												},
												{
													label: "JSON",
													id: "json",
													content: (
														<Textarea
															value={this.state.prizeDistributionJson}
															onChange={({ detail }) =>
																this.handleJsonChange(detail.value)
															}
															placeholder='[
																	{ "place": 1, "prize": "1000" },
																	{ "place": 2, "prize": "500" },
																	{ "place": 3, "prize": "250" }
															]'
															rows={10}
														/>
													),
												},
											]}
										/>
									</FormField>
								)}
							</SpaceBetween>
						</Container>
						)}

						{/* Требования к участникам */}
						{tournamentsEditModal.modalType === "tournaments" && (
							<Container header={<Box variant="h2">Participant Requirements</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										label="VIP Level Requirement"
										description="Minimum VIP level to participate (optional)"
										stretch
									>
										<Select
											selectedOption={
												tournamentsEditModal.vip_level !== null
													? {
														value: tournamentsEditModal.vip_level,
														label: `Level ${tournamentsEditModal.vip_level}`,
													}
													: null
											}
											onChange={({ detail }) =>
												this.updateParam(
													"vip_level",
													detail.selectedOption ? detail.selectedOption.value : null
												)
											}
											options={[...Array(11)].map((_, i) => ({
												value: i,
												label: `Level ${i}`,
											}))}
											placeholder="No VIP requirement"
										/>
									</FormField>

									<FormField
										label="Deposits Count Required"
										description="Number of deposits to participate (0 for all players)"
										stretch
									>
										<Input
											type="number"
											value={tournamentsEditModal.deposit_count}
											onChange={({ detail }) =>
												this.updateParam(
													"deposit_count",
													detail.value === ""
														? ""
														: Math.max(0, Math.min(500, Number(detail.value)))
												)
											}
											placeholder="1"
											inputMode="numeric"
										/>
									</FormField>

									<FormField
										label="Promo Scope"
										description="Where to display the tournament promo"
										stretch
									>
										<Select
											selectedOption={{
												value: tournamentsEditModal.promo_scope,
												label:
													tournamentsEditModal.promo_scope === "all_games"
														? "All Games"
														: "Tournament Games",
											}}
											onChange={({ detail }) =>
												this.updateParam("promo_scope", detail.selectedOption.value)
											}
											options={[
												{ value: "all_games", label: "All Games" },
												{ value: "tournament_games", label: "Tournament Games" },
											]}
										/>
									</FormField>
								</SpaceBetween>
							</Container>
						)}


						{shouldShowBotConfig && (
							<Container header={<Box variant="h2">Bot Configuration</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										label="Bot Config Enabled"
										description={`Enable bots for the tournament and configure up to ${MAX_TOURNAMENT_BOTS} bot slots`}
										stretch
									>
										<Toggle
											checked={botConfig.enabled}
											onChange={({ detail }) => this.handleBotConfigToggle(detail.checked)}
										>
											{botConfig.enabled ? "Enabled" : "Disabled"}
										</Toggle>
									</FormField>

									<Tabs
										activeTabId={this.state.botConfigTabId}
										onChange={this.handleBotConfigTabChange}
										tabs={[
											{
												label: "Regular",
												id: "regular",
												content: (
													<SpaceBetween direction="vertical" size="m">
														<Box color="text-body-secondary">
															{isDailyRaceTournament
																? "Add up to 10 daily race bots with target ranges. Target values are multiplied by 100 before saving. A new bot starts betting right after saving."
																: isSumOfWinningsTournament
																	? "Add, edit, or remove tournament bots. The list is limited to 10 bots and slots are reindexed automatically. Every Bet Interval a bot gets a random win from the Win Amount range until it reaches the target winnings. Nothing is credited during pauses."
																	: "Add, edit, or remove tournament bots. The list is limited to 10 bots and slots are reindexed automatically."}
														</Box>
														<Table
															columnDefinitions={isDailyRaceTournament ? [
																{
																	id: "slot",
																	header: "Slot",
																	cell: item => item.slot,
																	width: 80,
																},
																{
																	id: "targetMin",
																	header: "Target Min",
																	cell: item => (
																		<Input
																			type="number"
																			value={item.target.min}
																			onChange={({ detail }) =>
																				this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "target")
																			}
																			placeholder="9000000"
																		/>
																	),
																	width: 180,
																},
																{
																	id: "targetMax",
																	header: "Target Max",
																	cell: item => (
																		<Input
																			type="number"
																			value={item.target.max}
																			onChange={({ detail }) =>
																				this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "target")
																			}
																			placeholder="12000000"
																		/>
																	),
																	width: 180,
																},
																{
																	id: "actions",
																	header: "Actions",
																	cell: item => (
																		<Button
																			variant="inline-link"
																			onClick={() => this.removeBotConfigBot(item.slot - 1)}
																		>
																			Remove bot
																		</Button>
																	),
																	width: 160,
																},
															] : [
																{
																	id: "slot",
																	header: "Slot",
																	cell: item => item.slot,
																	width: 80,
																},
																isSumOfWinningsTournament
																	? {
																			id: "targetWinnings",
																			header: "Target Winnings (RUB)",
																			cell: item => (
																				<Input
																					type="number"
																					value={item.targetWinnings}
																					step="any"
																					onChange={({ detail }) =>
																						this.updateBotConfigBotField(item.slot - 1, "targetWinnings", detail.value)
																					}
																					placeholder="50000"
																				/>
																			),
																			width: 180,
																		}
																	: {
																			id: "targetBets",
																			header: "Target Bets",
																			cell: item => (
																				<Input
																					type="number"
																					value={item.targetBets}
																					onChange={({ detail }) =>
																						this.updateBotConfigBotField(item.slot - 1, "targetBets", detail.value)
																					}
																					placeholder="500"
																				/>
																			),
																			width: 160,
																		},
																...(isSumOfWinningsTournament
																	? [
																			{
																				id: "winAmount",
																				header: "Win Amount (RUB)",
																				cell: item => (
																					<SpaceBetween direction="horizontal" size="xs">
																						<Input
																							type="number"
																							value={item.winAmount.min}
																							step="any"
																							onChange={({ detail }) =>
																								this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "winAmount")
																							}
																							placeholder="Min"
																						/>
																						<Input
																							type="number"
																							value={item.winAmount.max}
																							step="any"
																							onChange={({ detail }) =>
																								this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "winAmount")
																							}
																							placeholder="Max"
																						/>
																					</SpaceBetween>
																				),
																				width: 260,
																			},
																		]
																	: []),
																{
																	id: "betInterval",
																	header: "Bet Interval (sec)",
																	cell: item => (
																		<SpaceBetween direction="horizontal" size="xs">
																			<Input
																				type="number"
																				value={item.betInterval.min}
																				step="any"
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "betInterval")
																				}
																				placeholder="Min"
																			/>
																			<Input
																				type="number"
																				value={item.betInterval.max}
																				step="any"
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "betInterval")
																				}
																				placeholder="Max"
																			/>
																		</SpaceBetween>
																	),
																	width: 260,
																},
																{
																	id: "pauseDuration",
																	header: "Pause Duration (min)",
																	cell: item => (
																		<SpaceBetween direction="horizontal" size="xs">
																			<Input
																				type="number"
																				value={item.pauseDuration.min}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "pauseDuration")
																				}
																				placeholder="Min"
																			/>
																			<Input
																				type="number"
																				value={item.pauseDuration.max}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "pauseDuration")
																				}
																				placeholder="Max"
																			/>
																		</SpaceBetween>
																	),
																	width: 280,
																},
																{
																	id: "pausesPerDay",
																	header: "Pauses Per Day",
																	cell: item => (
																		<SpaceBetween direction="horizontal" size="xs">
																			<Input
																				type="number"
																				value={item.pausesPerDay.min}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "pausesPerDay")
																				}
																				placeholder="Min"
																			/>
																			<Input
																				type="number"
																				value={item.pausesPerDay.max}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "pausesPerDay")
																				}
																				placeholder="Max"
																			/>
																		</SpaceBetween>
																	),
																	width: 240,
																},
																{
																	id: "startDelayMinutes",
																	header: "Start Delay (min)",
																	cell: item => (
																		<SpaceBetween direction="horizontal" size="xs">
																			<Input
																				type="number"
																				value={item.startDelayMinutes.min}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "min", detail.value, "startDelayMinutes")
																				}
																				placeholder="Min"
																			/>
																			<Input
																				type="number"
																				value={item.startDelayMinutes.max}
																				onChange={({ detail }) =>
																					this.updateBotConfigBotField(item.slot - 1, "max", detail.value, "startDelayMinutes")
																				}
																				placeholder="Max"
																			/>
																		</SpaceBetween>
																	),
																	width: 240,
																},
																{
																	id: "actions",
																	header: "Actions",
																	cell: item => (
																		<Button
																			variant="inline-link"
																			onClick={() => this.removeBotConfigBot(item.slot - 1)}
																		>
																			Remove bot
																		</Button>
																	),
																	width: 160,
																},
															]}
															items={botConfig.bots}
															empty={
																<Box textAlign="center" color="inherit">
																	No bots added
																</Box>
															}
															footer={
																<Button
																	variant="link"
																	onClick={this.addBotConfigBot}
																	disabled={botConfig.bots.length >= MAX_TOURNAMENT_BOTS}
																>
																	Add bot
																</Button>
															}
														/>
													</SpaceBetween>
												),
											},
											{
												label: "JSON",
												id: "json",
												content: (
													<SpaceBetween direction="vertical" size="m">
														<Box color="text-body-secondary">
															Paste the full bot config JSON. Valid changes are synced immediately and saved with the main Save button.
														</Box>
														<SpaceBetween direction="horizontal" size="xs">
															<Button onClick={this.copyBotConfigSelection}>
																Copy JSON
															</Button>
														</SpaceBetween>
														<FormField
															label="Bot config JSON"
															description={isDailyRaceTournament
																? "Fields: enabled, target. Target values are multiplied by 100 before saving."
																: isSumOfWinningsTournament
																	? "Fields: enabled, targetWinnings, winAmount, betInterval, pauseDuration, pausesPerDay, startDelayMinutes. Target winnings and win amount are in rubles and are multiplied by 100 before saving."
																	: "Fields: enabled, targetBets, betInterval, pauseDuration, pausesPerDay, startDelayMinutes"}
															errorText={this.state.botConfigError}
															stretch
														>
															<Textarea
																value={this.state.botConfigDraft}
																onChange={({ detail }) =>
																	this.handleBotConfigDraftChange(detail.value)
																}
																placeholder={isDailyRaceTournament
																	? `{\n  "enabled": true,\n  "bots": [\n    {\n      "slot": 1,\n      "uid": null,\n      "target": { "min": 9000000, "max": 12000000 }\n    }\n  ]\n}`
																: isSumOfWinningsTournament
																	? `{\n  "enabled": true,\n  "bots": [\n    {\n      "slot": 1,\n      "targetWinnings": 50000,\n      "winAmount": { "min": 10, "max": 2000 },\n      "betInterval": { "min": 60, "max": 180 },\n      "pauseDuration": { "min": 5, "max": 15 },\n      "pausesPerDay": { "min": 1, "max": 2 },\n      "startDelayMinutes": { "min": 0, "max": 30 }\n    }\n  ]\n}`
																	: `{\n  "enabled": true,\n  "bots": [\n    {\n      "slot": 1,\n      "targetBets": 500,\n      "betInterval": { "min": 60, "max": 180 },\n      "pauseDuration": { "min": 5, "max": 15 },\n      "pausesPerDay": { "min": 1, "max": 2 },\n      "startDelayMinutes": { "min": 0, "max": 30 }\n    }\n  ]\n}`}
																rows={18}
															/>
														</FormField>
													</SpaceBetween>
												),
											},
										]}
									/>
								</SpaceBetween>
							</Container>
						)}

						{/* Даты проведения */}
						{tournamentsEditModal.modalType === "tournaments" && (
							<Container header={<Box variant="h2">Tournament Dates</Box>}>
								<SpaceBetween direction="vertical" size="m">
									{tournamentsEditModal.modalType === "tournaments" && (
										<FormField
											label="From/To"
											description="Select the start and end dates for the tournament"
											stretch
										>
											<DateRangePicker
												onChange={({ detail }) => {
													setTournamentsEditModal({
														...tournamentsEditModal,
														from: detail.value.startDate.split("+")[0],
														to: detail.value.endDate.split("+")[0],
														type: detail.value.type,
													});
												}}
												value={{
													startDate: tournamentsEditModal.from,
													endDate: tournamentsEditModal.to,
													type: "absolute",
												}}
												relativeOptions={[]}
												isValidRange={range => {
													if (range.type === "absolute") {
														const startDateWithoutTime = range.startDate.split("T")[0];
														const endDateWithoutTime = range.endDate.split("T")[0];
														if (!startDateWithoutTime || !endDateWithoutTime) {
															return {
																valid: false,
																errorMessage:
																	"The selected date range is incomplete. Select a start and end date for the date range.",
															};
														}
														if (
															new Date(range.startDate) - new Date(range.endDate) >
															0
														) {
															return {
																valid: false,
																errorMessage:
																	"The selected date range is invalid. The start date must be before the end date.",
															};
														}
													} else {
														return { valid: false };
													}
												}}
												i18nStrings={{
													todayAriaLabel: "Today",
													nextMonthAriaLabel: "Next month",
													previousMonthAriaLabel: "Previous month",
													customRelativeRangeDurationLabel: "Duration",
													customRelativeRangeDurationPlaceholder: "Enter duration",
													customRelativeRangeOptionLabel: "Custom range",
													customRelativeRangeOptionDescription:
														"Define a custom range in the past",
													customRelativeRangeUnitLabel: "Unit of time",
													formatRelativeRange: ({ amount, unit, startDate, endDate }) => {
														const start = new Date(startDate);
														const end = new Date(endDate);

														const locale = "en-GB";
														const dateOptions = {
															year: "numeric",
															month: "long",
															day: "numeric",
														};
														const timeOptions = {
															hour: "2-digit",
															minute: "2-digit",
															second: "2-digit",
														};

														const formattedStart =
															start.toLocaleDateString(locale, dateOptions) +
															" " +
															start.toLocaleTimeString(locale, timeOptions);
														const formattedEnd =
															end.toLocaleDateString(locale, dateOptions) +
															" " +
															end.toLocaleTimeString(locale, timeOptions);

														return `${formattedStart} - ${formattedEnd}`;
													},
													formatUnit: (unit, amount) =>
														`${unit}${amount > 1 ? "s" : ""}`,
													dateTimeConstraintText:
														"Date and time must be within the selected range.",
													renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
														`Selected from ${startDate} to ${endDate}`,
													labelRelativeRange: "Relative range",
													labelAbsoluteRange: "Absolute range",
													dateTimePickerAriaLabel: "Date and time picker",
													dateTimePickerLabel: "Date and Time",
													clearButtonAriaLabel: "Clear date range",
													relativeModeTitle: "Relative mode",
													absoluteModeTitle: "Absolute mode",
													cancelButtonLabel: "Cancel",
													applyButtonLabel: "Apply",
												}}
												placeholder="From/to"
											/>
										</FormField>
									)}
								</SpaceBetween>
							</Container>
						)}
					</SpaceBetween>
				}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="m">
							<Button
								variant="normal"
								onClick={() =>
									setTournamentsEditModal({ ...tournamentsEditModal, show: false })
								}
							>
								Close
							</Button>
							<Button
								variant="primary"
								disabled={this.state.loading || this.state.jsonError || this.state.botConfigError}
								onClick={this.updateTournament}
							>
								Save
							</Button>
						</SpaceBetween>
					</Box>
				}
			/>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const tournamentsEditModal = useRecoilValue(tournamentsEditModalStore);
		const setTournamentsEditModal = useSetRecoilState(tournamentsEditModalStore);
		const tournaments = useRecoilValue(tournamentsStore);
		const setTournaments = useSetRecoilState(tournamentsStore);
		const game = useRecoilValue(gameStore);

		return (
			<Component
				{...props}
				tournaments={tournaments}
				setTournaments={setTournaments}
				setTournamentsEditModal={setTournamentsEditModal}
				tournamentsEditModal={tournamentsEditModal}
				game={game}
			/>
		);
	};
}

export default withRecoil(TournamentsEditModal);
